import { AppsManagementResponse, CMSResponse } from '../../types/common.type';
import {
    inject,
    Injectable,
    makeStateKey,
    PLATFORM_ID,
    TransferState,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    EventData,
    IEvent,
    IEventPage,
    IParamsEventPage,
} from '@shared/data/event.data';
import { Observable, of, tap } from 'rxjs';
import { environment } from '@environment/environment';
import { formatDate, isPlatformServer } from '@angular/common';
import { ValueGlobalService } from '@services/global/value.global.service';

@Injectable({
    providedIn: 'root',
})
export class EventService extends EventData {
    private HttpClient = inject(HttpClient);
    private transferState = inject(TransferState);
    private platformId = inject<Object>(PLATFORM_ID);

    readonly #valueGlobalService = inject(ValueGlobalService);
    readonly localeDefaultService = this.#valueGlobalService.locale;

    constructor() {
        super();
    }

    override GetEventPage(
        params?: IParamsEventPage
    ): Observable<AppsManagementResponse<IEvent[]>> {
        const { type, year, month, offset, limit } = params;
        const KEY = makeStateKey<AppsManagementResponse<IEvent[]>>(
            `event-page-${type}-${year}-${month}-${offset}-${limit}`
        );

        if (this.transferState.hasKey(KEY)) {
            const data = this.transferState.get(KEY, null!);
            this.transferState.remove(KEY);
            return of(data);
        }

        let url = `${environment.clientUrl}/events/${type}?offset=${offset}&limit=${limit}`;
        return this.HttpClient.get<AppsManagementResponse<IEvent[]>>(url, {
            params: {
                year,
                month,
            },
        }).pipe(
            tap((data) => {
                if (isPlatformServer(this.platformId)) {
                    this.transferState.set(KEY, data);
                }
            })
        );
    }

    override GetDetailEvent(slug: string): Observable<CMSResponse<IEvent>> {
        const KEY = makeStateKey<CMSResponse<IEvent>>(`event-detail-${slug}`);

        if (this.transferState.hasKey(KEY)) {
            const data = this.transferState.get(KEY, null!);
            this.transferState.remove(KEY);
            return of(data);
        }

        let url = `${environment.clientUrl}/events/${slug}`;
        return this.HttpClient.get<CMSResponse<IEvent>>(url).pipe(
            tap((data) => {
                if (isPlatformServer(this.platformId)) {
                    this.transferState.set(KEY, data);
                }
            })
        );
    }

    override GetTimeLine(
        type: string = 'internal'
    ): Observable<CMSResponse<string[]>> {
        const KEY = makeStateKey<CMSResponse<string[]>>(
            `event-timeline-${type}`
        );

        if (this.transferState.hasKey(KEY)) {
            const data = this.transferState.get(KEY, null!);
            this.transferState.remove(KEY);
            return of(data);
        }

        let url = `${environment.clientUrl}/events/${type}/years`;
        return this.HttpClient.get<CMSResponse<string[]>>(url).pipe(
            tap((data) => {
                if (isPlatformServer(this.platformId)) {
                    this.transferState.set(KEY, data);
                }
            })
        );
    }

    formatDate(date: string): string {
        if (this.localeDefaultService()?.code === 'vi') {
            return formatDate(date, "d, 'Tháng' M, y", 'en-US');
        } else {
            return formatDate(date, 'd, MMMM yyyy', 'en-US');
        }
    }
}
